import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { Volume2, ArrowLeft, CalendarIcon, Loader2, Tag, Check, Upload, Image } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingPage() {
  const { speakerId } = useParams();
  const navigate = useNavigate();
  const [speaker, setSpeaker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [idImage, setIdImage] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [returningCustomer, setReturningCustomer] = useState(null);
  const [checkingCustomer, setCheckingCustomer] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    event_duration_days: "1",
    delivery_address: "",
    special_requirements: "",
    wire_requirements: "",
    quantity: "1"
  });

  useEffect(() => {
    fetchSpeaker();
    fetchBookedDates();
  }, [speakerId]);

  const fetchSpeaker = async () => {
    try {
      const response = await axios.get(`${API}/speakers/${speakerId}`);
      setSpeaker(response.data);
    } catch (error) {
      console.error("Error fetching speaker:", error);
      toast.error("Equipment not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookedDates = async () => {
    try {
      const response = await axios.get(`${API}/speakers/${speakerId}/booked-dates`);
      setBookedDates(response.data.booked_dates || []);
    } catch (error) {
      console.error("Error fetching booked dates:", error);
    }
  };

  const isDateBooked = (dateToCheck) => {
    const dateStr = format(dateToCheck, "yyyy-MM-dd");
    return bookedDates.includes(dateStr);
  };

  const checkReturningCustomer = async (email) => {
    if (!email || !email.includes("@")) return;
    setCheckingCustomer(true);
    try {
      const res = await axios.get(`${API}/customers/check`, { params: { email } });
      if (res.data.returning && res.data.id_on_file) {
        setReturningCustomer(res.data);
        if (res.data.customer_name && !formData.customer_name) {
          setFormData(f => ({ ...f, customer_name: res.data.customer_name }));
        }
        if (res.data.customer_phone && !formData.customer_phone) {
          setFormData(f => ({ ...f, customer_phone: res.data.customer_phone }));
        }
        toast.success("Welcome back! Your ID is already on file.");
      } else {
        setReturningCustomer(null);
      }
    } catch {
      setReturningCustomer(null);
    } finally {
      setCheckingCustomer(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculateSubtotal = () => {
    if (!speaker) return 0;
    return speaker.price_per_day * parseInt(formData.event_duration_days) * parseInt(formData.quantity);
  };

  const calculateDiscount = () => {
    if (!discountApplied) return 0;
    return calculateSubtotal() * (discountApplied.discount_percent / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount();
  };

  const handleIdUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      setIdImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setIdPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) return;
    
    setApplyingDiscount(true);
    try {
      const response = await axios.get(`${API}/discount-codes/validate/${discountCode}`);
      setDiscountApplied(response.data);
      toast.success(`Discount applied: ${response.data.discount_percent}% off!`);
    } catch (error) {
      toast.error("Invalid or expired discount code");
      setDiscountApplied(null);
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error("Please select an event date");
      return;
    }

    if (!idImage && !returningCustomer?.id_on_file) {
      toast.error("Please upload a photo of your ID");
      return;
    }

    setSubmitting(true);

    try {
      // Convert ID image to base64 if provided
      let idBase64 = null;
      if (idImage) {
        const reader = new FileReader();
        idBase64 = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(idImage);
        });
      }

      const bookingData = {
        speaker_id: speakerId,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        event_date: format(date, "yyyy-MM-dd"),
        event_duration_days: parseInt(formData.event_duration_days),
        delivery_address: formData.delivery_address,
        special_requirements: [formData.wire_requirements ? `Wires: ${formData.wire_requirements}` : null, formData.special_requirements].filter(Boolean).join(' | ') || null,
        quantity: speaker?.model === 'Bundle' ? 1 : parseInt(formData.quantity),
        discount_code: discountApplied ? discountApplied.code : null,
        id_image: idBase64
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      toast.success("Request submitted successfully!");
      navigate(`/booking/confirmation/${response.data.id}`);
    } catch (error) {
      console.error("Error creating request:", error);
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#141414] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF0033] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#141414]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm uppercase tracking-wider">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <img src="https://static.prod-images.emergentagent.com/jobs/641f6878-5cef-40eb-8d31-2194fc50afac/images/9204c88f36c8aba1418f399ed70957b9e9b435b9671cfdca88f8cc5e5e0a2467.png" alt="The Humble Hires" className="h-20" />
          </div>
        </div>
      </header>

      <div className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left: Speaker Info */}
            <div className="lg:sticky lg:top-24 lg:h-fit">
              <div className="neon-card overflow-hidden">
                <div className="relative h-64 lg:h-80">
                  <img 
                    src={speaker?.image_url} 
                    alt={speaker?.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-wider text-[#FF0033] mb-2 mono">{speaker?.model}</p>
                  <h1 className="text-2xl font-bold mb-4" data-testid="speaker-name">{speaker?.name}</h1>
                  <p className="text-white/70 mb-6">{speaker?.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-6">
                    {speaker?.specs?.power && <span className="spec-tag">{speaker.specs.power}</span>}
                    {speaker?.specs?.size && <span className="spec-tag">{speaker.specs.size}</span>}
                    {speaker?.specs?.max_spl && <span className="spec-tag">{speaker.specs.max_spl}</span>}
                    {speaker?.specs?.type && <span className="spec-tag">{speaker.specs.type}</span>}
                    {speaker?.specs?.frequency && <span className="spec-tag">{speaker.specs.frequency}</span>}
                  </div>

                  <div className="border-t border-white/10 pt-6">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-white/70">Price per day</span>
                      <span className="text-xl font-bold">£{speaker?.price_per_day}</span>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-white/70">Duration</span>
                      <span>{formData.event_duration_days} day(s)</span>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-white/70">Quantity</span>
                      <span>{formData.quantity} unit(s)</span>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-white/70">Subtotal</span>
                      <span>£{calculateSubtotal().toFixed(2)}</span>
                    </div>
                    {discountApplied && (
                      <div className="flex justify-between items-center mb-4 text-[#39FF14]">
                        <span>Discount ({discountApplied.discount_percent}%)</span>
                        <span>-£{calculateDiscount().toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-4 border-t border-white/10">
                      <span className="text-lg font-bold">Estimated Total</span>
                      <span className="text-2xl font-bold text-[#FF0033]" data-testid="total-price">
                        £{calculateTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Booking Form */}
            <div>
              <div className="mb-8">
                <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-2">Request Equipment</p>
                <h2 className="text-3xl font-bold tracking-tight">Submit Your Request</h2>
                <p className="text-white/70 mt-2">Fill in your details and we'll get back to you to confirm availability.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Info */}
                <div className="glass-panel p-6 space-y-4">
                  <h3 className="text-sm uppercase tracking-wider text-white/70 mb-4">Personal Information</h3>
                  
                  <div>
                    <Label htmlFor="customer_name" className="text-xs uppercase tracking-wider text-white/70 mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      required
                      className="bg-black/50 border-0 border-b border-white/20 rounded-none focus:border-[#FF0033] focus:ring-0"
                      placeholder="John Doe"
                      data-testid="input-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="customer_email" className="text-xs uppercase tracking-wider text-white/70 mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="customer_email"
                      name="customer_email"
                      type="email"
                      value={formData.customer_email}
                      onChange={handleInputChange}
                      onBlur={(e) => checkReturningCustomer(e.target.value)}
                      required
                      className="bg-black/50 border-0 border-b border-white/20 rounded-none focus:border-[#FF0033] focus:ring-0"
                      placeholder="john@example.com"
                      data-testid="input-email"
                    />
                    {checkingCustomer && <p className="text-xs text-white/50 mt-1">Checking...</p>}
                    {returningCustomer && (
                      <p className="text-xs text-[#39FF14] mt-1" data-testid="returning-customer-msg">Welcome back! {returningCustomer.booking_count} previous booking(s) found.</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="customer_phone" className="text-xs uppercase tracking-wider text-white/70 mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="customer_phone"
                      name="customer_phone"
                      type="tel"
                      value={formData.customer_phone}
                      onChange={handleInputChange}
                      required
                      className="bg-black/50 border-0 border-b border-white/20 rounded-none focus:border-[#FF0033] focus:ring-0"
                      placeholder="+44 7700 900000"
                      data-testid="input-phone"
                    />
                  </div>

                  {/* ID Upload */}
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-white/70 mb-2 block">
                      Photo ID (Passport/Driving Licence) {returningCustomer?.id_on_file ? '' : '*'}
                    </Label>
                    {returningCustomer?.id_on_file ? (
                      <div className="mt-2 flex items-center gap-3 p-4 bg-[#39FF14]/10 border border-[#39FF14]/20 rounded" data-testid="id-on-file">
                        <Check className="w-5 h-5 text-[#39FF14]" />
                        <div>
                          <p className="text-sm font-medium text-[#39FF14]">ID already on file</p>
                          <p className="text-xs text-white/50">No need to upload again</p>
                        </div>
                      </div>
                    ) : (
                    <div className="mt-2">
                      {idPreview ? (
                        <div className="relative">
                          <img 
                            src={idPreview} 
                            alt="ID Preview" 
                            className="w-full h-40 object-cover rounded border border-white/20"
                          />
                          <button
                            type="button"
                            onClick={() => { setIdImage(null); setIdPreview(null); }}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded text-xs"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-32 border border-dashed border-white/20 cursor-pointer hover:border-[#FF0033] transition-colors">
                          <Upload className="w-8 h-8 text-white/50 mb-2" />
                          <span className="text-white/50 text-sm">Click to upload ID</span>
                          <span className="text-white/20 text-xs mt-1">Max 10MB</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleIdUpload}
                            className="hidden"
                            data-testid="input-id-upload"
                          />
                        </label>
                      )}
                    </div>
                    )}
                    {!returningCustomer?.id_on_file && <p className="text-white/50 text-xs mt-2">Required for security purposes</p>}
                  </div>
                </div>

                {/* Event Details */}
                <div className="glass-panel p-6 space-y-4">
                  <h3 className="text-sm uppercase tracking-wider text-white/70 mb-4">Event Details</h3>
                  
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-white/70 mb-2 block">
                      Requested Date *
                    </Label>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal bg-black/50 border-0 border-b border-white/20 rounded-none hover:bg-black/50 hover:border-[#FF0033]"
                          data-testid="date-picker-trigger"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-[#FF0033]" />
                          {date ? format(date, "PPP") : <span className="text-white/50">Select a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#1A1A1A] border border-white/10" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(newDate) => {
                            setDate(newDate);
                            setCalendarOpen(false);
                          }}
                          disabled={(d) => d < new Date() || isDateBooked(d)}
                          initialFocus
                          className="bg-[#1A1A1A]"
                          data-testid="calendar"
                        />
                      </PopoverContent>
                    </Popover>
                    {bookedDates.length > 0 && (
                      <p className="text-xs text-[#FF0055]/60 mt-2">Some dates are unavailable due to existing bookings</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-white/70 mb-2 block">
                        Duration (Days) *
                      </Label>
                      <Select 
                        value={formData.event_duration_days} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, event_duration_days: value }))}
                      >
                        <SelectTrigger 
                          className="bg-black/50 border-0 border-b border-white/20 rounded-none focus:border-[#FF0033] focus:ring-0"
                          data-testid="duration-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border border-white/10">
                          {[1, 2, 3, 4, 5, 6, 7].map(day => (
                            <SelectItem key={day} value={day.toString()} className="hover:bg-[#FF0033]/10">
                              {day} {day === 1 ? 'day' : 'days'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs uppercase tracking-wider text-white/70 mb-2 block">
                        Quantity *
                      </Label>
                      {speaker?.model === 'Bundle' ? (
                        <div className="bg-black/50 border-0 border-b border-white/20 py-3 text-white">
                          1 Bundle
                        </div>
                      ) : (
                        <Select 
                          value={formData.quantity} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, quantity: value }))}
                        >
                          <SelectTrigger 
                            className="bg-black/50 border-0 border-b border-white/20 rounded-none focus:border-[#FF0033] focus:ring-0"
                            data-testid="quantity-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1A1A1A] border border-white/10">
                            {[1, 2, 3, 4].map(qty => (
                              <SelectItem key={qty} value={qty.toString()} className="hover:bg-[#FF0033]/10">
                                {qty} unit(s)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                </div>

                {/* Delivery */}
                <div className="glass-panel p-6 space-y-4">
                  <h3 className="text-sm uppercase tracking-wider text-white/70 mb-4">Delivery Information</h3>
                  
                  <div>
                    <Label htmlFor="delivery_address" className="text-xs uppercase tracking-wider text-white/70 mb-2 block">
                      Delivery Address *
                    </Label>
                    <Textarea
                      id="delivery_address"
                      name="delivery_address"
                      value={formData.delivery_address}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      className="bg-black/50 border-0 border-b border-white/20 rounded-none focus:border-[#FF0033] focus:ring-0 resize-none"
                      placeholder="Enter full delivery address including postcode"
                      data-testid="input-address"
                    />
                  </div>

                  <div>
                    <Label htmlFor="wire_requirements" className="text-xs uppercase tracking-wider text-white/70 mb-2 block">
                      Wire / Cable Requirements
                    </Label>
                    <Textarea
                      id="wire_requirements"
                      name="wire_requirements"
                      value={formData.wire_requirements || ""}
                      onChange={handleInputChange}
                      rows={3}
                      className="bg-black/50 border-0 border-b border-white/20 rounded-none focus:border-[#FF0033] focus:ring-0 resize-none"
                      placeholder="e.g. XLR cables, extension leads, aux cable, RCA to jack..."
                      data-testid="input-wires"
                    />
                    <p className="text-xs text-white/50 mt-2">Not sure what wires you need? <a href="https://wa.me/447543710680" target="_blank" rel="noopener noreferrer" className="text-[#FF0033] hover:underline">Message us on WhatsApp</a> and we'll help you out.</p>
                  </div>

                  <div>
                    <Label htmlFor="special_requirements" className="text-xs uppercase tracking-wider text-white/70 mb-2 block">
                      Special Requirements (Optional)
                    </Label>
                    <Textarea
                      id="special_requirements"
                      name="special_requirements"
                      value={formData.special_requirements}
                      onChange={handleInputChange}
                      rows={3}
                      className="bg-black/50 border-0 border-b border-white/20 rounded-none focus:border-[#FF0033] focus:ring-0 resize-none"
                      placeholder="Any other specific requirements for your event?"
                      data-testid="input-requirements"
                    />
                  </div>
                </div>

                {/* Discount Code */}
                <div className="glass-panel p-6">
                  <h3 className="text-sm uppercase tracking-wider text-white/70 mb-4">Discount Code</h3>
                  <div className="flex gap-3">
                    <div className="flex-1 relative">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                      <Input
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        disabled={discountApplied}
                        className="bg-black/50 border-0 border-b border-white/20 rounded-none focus:border-[#FF0033] focus:ring-0 pl-10 uppercase"
                        data-testid="input-discount-code"
                      />
                    </div>
                    {discountApplied ? (
                      <div className="flex items-center gap-2 text-[#39FF14]">
                        <Check className="w-5 h-5" />
                        <span className="text-sm">{discountApplied.discount_percent}% OFF</span>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        onClick={applyDiscountCode}
                        disabled={applyingDiscount || !discountCode.trim()}
                        className="btn-secondary px-6"
                        data-testid="apply-discount"
                      >
                        {applyingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Submit */}
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full btn-primary py-6 text-base"
                  data-testid="submit-booking"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting Request...
                    </>
                  ) : (
                    `Submit Request - £${calculateTotal().toFixed(2)}`
                  )}
                </Button>
                <p className="text-center text-white/50 text-sm mt-4">
                  Your request will be reviewed and we'll contact you to confirm availability
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
